import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Tooltip, CircularProgress,
  Snackbar, Alert, Chip, Divider, List, ListItem, ListItemText, ListItemSecondaryAction,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, ViewModule as KitIcon, Close as CloseIcon, RemoveCircle as RemoveIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const InventoryKits: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [kits, setKits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [allItems, setAllItems] = useState<any[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', description: '', category_id: '' });
  const [saving, setSaving] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedKit, setSelectedKit] = useState<any>(null);
  const [kitItems, setKitItems] = useState<any[]>([]);
  const [addItemId, setAddItemId] = useState('');
  const [addItemQty, setAddItemQty] = useState(1);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchKits = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(getApiUrl('/inventory-module/kits')); setKits(res.data.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const fetchMeta = useCallback(async () => {
    try {
      const [catRes, itemsRes] = await Promise.all([
        axios.get(getApiUrl('/inventory-module/categories')),
        axios.get(getApiUrl('/inventory-module/items'), { params: { limit: 500 } }),
      ]);
      setCategories(catRes.data.data || []);
      setAllItems(itemsRes.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchKits(); fetchMeta(); }, [fetchKits, fetchMeta]);

  const openDialog = (kit?: any) => {
    if (kit) { setEditingId(kit.id); setForm({ name: kit.name || '', description: kit.description || '', category_id: kit.category_id || '' }); }
    else { setEditingId(null); setForm({ name: '', description: '', category_id: '' }); }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSnackbar({ open: true, message: 'Nome obrigatório', severity: 'error' }); return; }
    try {
      setSaving(true);
      const payload = { ...form, category_id: form.category_id || null };
      if (editingId) await axios.put(getApiUrl(`/inventory-module/kits/${editingId}`), payload);
      else await axios.post(getApiUrl('/inventory-module/kits'), payload);
      setSnackbar({ open: true, message: editingId ? 'Atualizado!' : 'Criado!', severity: 'success' });
      setDialogOpen(false); fetchKits();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Desativar este kit?')) return;
    try { await axios.delete(getApiUrl(`/inventory-module/kits/${id}`)); setSnackbar({ open: true, message: 'Desativado!', severity: 'success' }); fetchKits(); }
    catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const openDetail = async (kit: any) => {
    try {
      const res = await axios.get(getApiUrl(`/inventory-module/kits/${kit.id}`));
      setSelectedKit(res.data.data);
      setKitItems(res.data.data?.items || []);
      setDetailOpen(true);
    } catch (e) { console.error(e); }
  };

  const handleAddItem = async () => {
    if (!addItemId || !selectedKit) return;
    try {
      await axios.post(getApiUrl(`/inventory-module/kits/${selectedKit.id}/items`), { item_id: addItemId, quantity: addItemQty });
      setSnackbar({ open: true, message: 'Item adicionado!', severity: 'success' });
      setAddItemId(''); setAddItemQty(1);
      const res = await axios.get(getApiUrl(`/inventory-module/kits/${selectedKit.id}`));
      setSelectedKit(res.data.data);
      setKitItems(res.data.data?.items || []);
      fetchKits();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
  };

  const handleRemoveItem = async (kitItemId: string) => {
    if (!selectedKit) return;
    try {
      await axios.delete(getApiUrl(`/inventory-module/kit-items/${kitItemId}`));
      setSnackbar({ open: true, message: 'Item removido!', severity: 'success' });
      const res = await axios.get(getApiUrl(`/inventory-module/kits/${selectedKit.id}`));
      setSelectedKit(res.data.data);
      setKitItems(res.data.data?.items || []);
      fetchKits();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KitIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Kits de Produtos</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar"><IconButton onClick={fetchKits}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            Novo Kit
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 700 }}>Kit</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Custo Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kits.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#9ca3af' }}>Nenhum kit cadastrado</TableCell></TableRow>
              ) : kits.map(kit => (
                <TableRow key={kit.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(kit)}>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{kit.name}</Typography></TableCell>
                  <TableCell>{kit.clinic_inventory_categories?.name || '-'}</TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{kit.description || '-'}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(kit.total_cost)}</Typography></TableCell>
                  <TableCell align="center">
                    <Chip label={kit.is_active ? 'Ativo' : 'Inativo'} size="small" sx={{ bgcolor: kit.is_active ? '#d1fae5' : '#f3f4f6', color: kit.is_active ? '#059669' : '#6b7280', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openDialog(kit)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Desativar"><IconButton size="small" onClick={() => handleDelete(kit.id)}><DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog criar/editar kit */}
      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          {editingId ? 'Editar Kit' : 'Novo Kit'}
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth required label="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} size="small" />
            <FormControl fullWidth size="small"><InputLabel>Categoria</InputLabel>
              <Select value={form.category_id} label="Categoria" onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                <MenuItem value="">Nenhuma</MenuItem>
                {categories.filter(c => c.is_active).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField fullWidth label="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} size="small" multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog detalhe do kit (itens) */}
      <Dialog fullScreen={isMobile} scroll="paper" open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          Kit: {selectedKit?.name}
          <IconButton onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Itens do Kit</Typography>
          {kitItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>Nenhum item neste kit</Typography>
          ) : (
            <List dense>
              {kitItems.map((ki: any) => (
                <ListItem key={ki.id} sx={{ borderBottom: '1px solid #f3f4f6' }}>
                  <ListItemText
                    primary={ki.clinic_inventory_items?.name || 'Item'}
                    secondary={`SKU: ${ki.clinic_inventory_items?.sku || '-'} | Qtd: ${ki.quantity} | Custo unit: ${formatCurrency(ki.clinic_inventory_items?.cost_price || 0)}`}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Remover"><IconButton size="small" onClick={() => handleRemoveItem(ki.id)}><RemoveIcon sx={{ color: '#ef4444' }} /></IconButton></Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Adicionar Item</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Item</InputLabel>
                <Select value={addItemId} label="Item" onChange={(e) => setAddItemId(e.target.value)}>
                  {allItems.map(item => <MenuItem key={item.id} value={item.id}>{item.name} ({item.sku || '-'})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Quantidade" type="number" size="small" value={addItemQty} onChange={(e) => setAddItemQty(Number(e.target.value))} inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button variant="contained" fullWidth onClick={handleAddItem} disabled={!addItemId}
                sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
                Adicionar
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
            <Typography variant="body2"><strong>Custo Total do Kit:</strong> {formatCurrency(selectedKit?.total_cost || 0)}</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailOpen(false)} sx={{ textTransform: 'none' }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryKits;
